import { useEffect, useRef } from "react";

/**
 * Subtle animated ticker-glyph background for the auth screens.
 *
 * A single `<canvas>` element rendered behind the auth card. Draws
 * drifting columns of ticker symbols + digits in the AURION gold and
 * a low-saturation purple. Uses `requestAnimationFrame` so it pauses
 * when the tab is hidden. `pointer-events-none` guarantees it never
 * intercepts clicks or focus.
 *
 * Perf notes:
 *   - one canvas, one rAF loop
 *   - column count scales with viewport width but capped ~120 columns
 *   - low alpha overlay each frame gives the trail effect cheaply
 */
const GLYPHS = "AAPL·NVDA·MSFT·TSLA·BTC·ETH·SOL·BNB·GLD·SPY·QQQ·0123456789·+·-··↑↓";
const CHARS = GLYPHS.split("");

export default function MatrixBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let raf = 0;
    let running = true;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fontSize = 14;
    let columns = 0;
    let drops = [];
    let colorPicks = [];

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.min(120, Math.floor(w / fontSize));
      drops = new Array(columns).fill(0).map(() => Math.random() * (h / fontSize));
      // 80% gold, 20% purple — matches AURION palette
      colorPicks = new Array(columns)
        .fill(0)
        .map(() => (Math.random() < 0.8 ? "gold" : "purple"));
    };

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
    };

    const tick = () => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      // low-alpha black overlay → trail
      ctx.fillStyle = "rgba(6, 6, 8, 0.09)";
      ctx.fillRect(0, 0, w, h);
      ctx.font = `${fontSize}px "JetBrains Mono", ui-monospace, monospace`;

      for (let i = 0; i < columns; i++) {
        const ch = CHARS[Math.floor(Math.random() * CHARS.length)];
        const x = i * fontSize + fontSize / 2;
        const y = drops[i] * fontSize;
        ctx.fillStyle =
          colorPicks[i] === "gold"
            ? "rgba(201, 162, 39, 0.55)"
            : "rgba(139, 92, 246, 0.35)";
        ctx.fillText(ch, x, y);

        if (y > h && Math.random() > 0.975) {
          drops[i] = 0;
          colorPicks[i] = Math.random() < 0.8 ? "gold" : "purple";
        }
        drops[i] += 1;
      }
      if (running) raf = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVis);
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="matrix-bg"
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 opacity-40"
    />
  );
}
